import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { riseIn } from '@shared/theme/animations';
import { IconArrowLeft } from '@shared/ui/icons';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(7)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  min-height: 32px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }

  &:hover > svg {
    transform: translateX(-3px);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const SectionTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

const SECTIONS: Record<string, { title: string; subtitle?: string }> = {
  contracts: {
    title: 'Договоры',
    subtitle: 'Реестр договоров и автоматическая правовая экспертиза по 7 категориям рисков.',
  },
  precontracts: {
    title: 'Преддоговорная работа',
    subtitle:
      'Незаключённые договоры на стадии проработки и согласования. Та же экспертиза рисков; после подписания перевод в реестр «Договоры».',
  },
  counterparties: {
    title: 'Контрагенты',
    subtitle:
      'Реестр юридических лиц с проверкой через ЕГРЮЛ. Договоры и претензии привязаны по ИНН.',
  },
  metrics: {
    title: 'Метрики качества',
    subtitle:
      'целевые показатели работы юр.агента. Точность ссылок, % принятых рисков, 0 пропусков сроков.',
  },
  claims: {
    title: 'Претензии и иски',
    subtitle: 'Реестр претензий и судебных дел. Расчёт неустойки, контроль процессуальных сроков.',
  },
  correspondence: {
    title: 'Корреспонденция',
    subtitle: 'Входящие письма от агента-диспетчера. Содержательная классификация и автодедлайны.',
  },
  deadlines: {
    title: 'Сроки',
    subtitle:
      'Единый реестр сроков по договорам, претензиям, корреспонденции и запросам госорганов.',
  },
  calculator: {
    title: 'Калькулятор',
    subtitle: 'Расчёт неустойки и процентов по ст.395 ГК РФ с ключевой ставкой ЦБ РФ.',
  },
  knowledge: {
    title: 'База знаний',
    subtitle: 'НПА, ГОСТы, эталонные формы САТКОМ и внутренние документы — единый корпус для RAG.',
  },
  compliance: {
    title: 'Проверка документа',
    subtitle: 'Проверка проектной документации на соответствие ГОСТам и НПА.',
  },
  chat: {
    title: 'Чат с агентом',
    subtitle: 'Правовые вопросы по корпоративной базе знаний.',
  },
  gov: {
    title: 'Госорганы',
    subtitle: 'Запросы ФНС, прокуратуры, Госстройнадзора, Ростехнадзора.',
  },
  monitoring: {
    title: 'Мониторинг НПА',
    subtitle: 'Изменения в законодательстве и судебная практика.',
  },
  lna: {
    title: 'Локальные нормативные акты',
    subtitle: 'Реестр приказов, положений, регламентов.',
  },
  documents: {
    title: 'Архив документов',
    subtitle:
      'Все когда-либо сгенерированные DOCX: ответы на претензии, перевыставления, протоколы разногласий.',
  },
  lawsuits: {
    title: 'Иск',
    subtitle: 'Карточка искового заявления, статус сборки DOCX и связи с претензией / договором.',
  },
  trash: {
    title: 'Корзина',
    subtitle: 'Удалённые документы и записи — восстановление в течение 30 дней.',
  },
};

export function StatutLayout() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  // /agents/statut/<section>
  const section = segments[2];
  const meta = section ? SECTIONS[section] : null;
  const isRoot = !section;
  const { can_read, can_write } = useAgentPermission('statut');

  return (
    <Wrap>
      <Header>
        {isRoot ? (
          <>
            <Title>
              ОСНОВА<span className="accent">.Юрист</span>
            </Title>
            <Subtitle>
              Юридический агент: договорная работа, претензии и иски, обработка корреспонденции,
              мониторинг законодательства, подготовка локальных нормативных актов.
            </Subtitle>
          </>
        ) : (
          <>
            <TopRow>
              <Back to="/agents/statut">
                <IconArrowLeft size={16} />
                <span>ОСНОВА.Юрист</span>
              </Back>
            </TopRow>
            <SectionTitle>{meta?.title ?? section}</SectionTitle>
            {meta?.subtitle && <Subtitle>{meta.subtitle}</Subtitle>}
          </>
        )}
      </Header>
      {can_read && !can_write && <ReadOnlyBanner />}
      <Outlet />
    </Wrap>
  );
}
